import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, ErrorBanner, Pill, fmt } from "../components/ui";
import {
  api,
  type CostPreview,
  type PersonalizedDraft,
  type RulePreview,
  type Template,
  type Throughput,
} from "../lib/api";

/** A local datetime typed in Eastern -> the UTC instant it refers to.
 *  Done by asking the browser what that wall-clock time is in New York on
 *  that date, so it stays correct across the daylight-saving change rather
 *  than assuming a fixed offset. */
function etToUtc(local: string): string {
  const [datePart, timePart] = local.split("T");
  const [y, mo, d] = datePart.split("-").map(Number);
  const [h, mi] = timePart.split(":").map(Number);
  // start from the naive instant, then correct by New York's offset that day
  const asUtc = Date.UTC(y, mo - 1, d, h, mi);
  const probe = new Date(asUtc);
  const inNy = new Date(
    probe.toLocaleString("en-US", { timeZone: "America/New_York" }),
  );
  const asIfUtc = new Date(probe.toLocaleString("en-US", { timeZone: "UTC" }));
  return new Date(asUtc + (asIfUtc.getTime() - inNy.getTime())).toISOString();
}

/** What the clock says in `zone` at the moment the Eastern window opens.
 *
 *  The engine judges every contact against one fixed window - the contact's
 *  own timezone is deliberately ignored, because it is the value we do not
 *  trust - so this is the local time a person in that zone is really texted
 *  at, not a promise about their own 11am. */
function localAtWindowOpen(zones: Array<{ zone: string; count: number }>) {
  const today = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
  }).format(new Date());
  const opens = new Date(etToUtc(`${today}T11:00`));

  const read = (zone: string) => {
    try {
      return new Intl.DateTimeFormat("en-GB", {
        timeZone: zone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(opens);
    } catch {
      return null; // a zone the browser does not know is not worth a claim
    }
  };

  const seen = zones
    .map((z) => ({ ...z, at: read(z.zone) }))
    .filter((z): z is { zone: string; count: number; at: string } => z.at !== null);
  if (!seen.length) return null;

  // The earliest local time is the one worth naming: 11am Eastern is 5am in
  // Honolulu, which is before any hour a person would call reasonable.
  return seen.reduce((a, b) => (a.at <= b.at ? a : b));
}

type Followup = {
  mode: "after" | "day";
  /** minutes after the message before it (mode "after") */
  after: number;
  /** day number and clock time (mode "day") */
  day: number;
  at: string;
  body: string;
};

const fmtDelay = (m: number) =>
  m % 60 === 0 ? `${m / 60} h` : m > 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`;

export function BlastNew() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const rule = useMemo(() => {
    try {
      return JSON.parse(params.get("rule") ?? "{}");
    } catch {
      return {};
    }
  }, [params]);

  const [name, setName] = useState("");
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [rate, setRate] = useState(240);
  const [preview, setPreview] = useState<RulePreview | null>(null);
  const [cost, setCost] = useState<CostPreview | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cap, setCap] = useState<number | null>(null);
  // What the send pipe has really managed, so the estimate below is not
  // built on a number somebody typed. Null until it loads; the estimate
  // falls back to the typed rate rather than showing nothing.
  const [capacity, setCapacity] = useState<Throughput | null>(null);
  // Empty means start as soon as it is launched. Entered in Eastern because
  // that is the zone the sending window is defined in - asking for a time in
  // the browser's zone would mean someone in Karachi scheduling 3pm and
  // getting 3pm there.
  const [startAt, setStartAt] = useState("");
  //: Build the list at the send time instead of now (single-message blasts).
  const [buildAtSend, setBuildAtSend] = useState(false);
  // Standby: sent only when the team has room, by the volume autopilot
  const [standby, setStandby] = useState(false);
  // A blast can be several messages over several days - that is how the
  // one-off workflows in GHL actually worked. The first goes out on launch,
  // the rest follow on their own day, and anyone who replies stops getting
  // them.
  // A follow-up either goes a set time after the message before it, per
  // person and the same day ("after"), or on a later day at a clock time
  // ("day"). Anyone who replies stops getting the rest either way.
  const [followups, setFollowups] = useState<Followup[]>([]);
  const [draft, setDraft] = useState("");
  // Individually-written messages. `personalize` swaps what the copy box
  // means: it stops being the message and becomes the fallback, which is
  // said on screen rather than left to be inferred.
  const [personalize, setPersonalize] = useState(false);
  const [brief, setBrief] = useState("");
  const [samples, setSamples] = useState<PersonalizedDraft[] | null>(null);
  const [sampling, setSampling] = useState(false);
  const [sampleError, setSampleError] = useState<string | null>(null);
  const [consentSource, setConsentSource] = useState("");
  const [consenting, setConsenting] = useState(false);
  // Safe by default: someone with a call booked, or blasted in the last few
  // days, is left out unless the operator unticks it. A blast texted Jimmy
  // James the day before his appointment (18 Sep 2026).
  const [skip, setSkip] = useState<Record<string, boolean>>({
    has_appointment: true,
    blasted_recently: true,
  });
  const [impact, setImpact] = useState<Awaited<
    ReturnType<typeof api.audienceImpact>
  > | null>(null);

  useEffect(() => {
    api
      .templates()
      .then(setTemplates)
      .catch((e) => setError(e.message));
    api.previewAudience(rule).then(setPreview).catch((e) => setError(e.message));
    api.throughput().then(setCapacity).catch(() => setCapacity(null));
    // what this send would collide with - a count alone does not say that a
    // third of them are mid-cadence and would hear from us twice today
  }, [rule]);

  // Re-counted whenever an exclusion is toggled, with the same filters the
  // send will apply, so the number on screen is the number that goes out.
  useEffect(() => {
    api.audienceImpact(rule, null, skip).then(setImpact).catch(() => setImpact(null));
  }, [rule, skip]);

  const body = templateId
    ? (templates.find((t) => t.id === templateId)?.variants[0]?.body ?? "")
    : draft;
  const matched = preview?.reachable ?? 0;
  const reachable = impact?.after_exclusions ?? matched;
  const noConsent = preview?.excluded?.no_consent ?? 0;

  // The number that will actually be messaged, which is what the cost, the
  // ETA and the confirmation all have to agree on.
  const willSend = cap ? Math.min(cap, reachable) : reachable;

  // The rate is a CEILING, not a throttle that can be opened wider. Nothing in
  // the send path reads a configured rate: throughput comes from how many
  // worker ticks run and how long each message takes at the gateway, measured
  // at about 290 a minute. Dividing by whatever someone typed told them 8,000
  // people would take 13 minutes when the honest answer was 28.
  //
  // So the estimate uses whichever is SLOWER - what was asked for, or what the
  // pipe has actually done.
  const effectiveRate = Math.min(rate, capacity?.sustained_per_minute || rate);
  const minutes = Math.max(1, Math.round(willSend / Math.max(1, effectiveRate)));
  const rateIsWishful =
    !!capacity && rate > capacity.sustained_per_minute && willSend > 0;

  useEffect(() => {
    if (!body || !willSend) return;
    api.cost(body, willSend).then(setCost).catch(() => setCost(null));
  }, [body, willSend]);

  const grantConsent = () => {
    setConsenting(true);
    api
      .grantConsent(rule, consentSource.trim())
      .then(() => {
        setConsentSource("");
        // re-count, so the number you are about to type to confirm is the
        // number that will actually be sent to
        return api.previewAudience(rule).then(setPreview);
      })
      .catch((e) => setError(e.message))
      .finally(() => setConsenting(false));
  };

  // Typing the number is deliberate friction. Above a few thousand people a
  // mis-click is expensive and irreversible, so the confirmation asks for
  // something you cannot do by accident.
  const confirmed = confirmText.trim() === String(willSend) && willSend > 0;

  const create = () => {
    setBusy(true);
    setError(null);

    // One message is a blast: a single send with exact progress and cancel.
    // More than one is a short campaign with a fixed audience - the same
    // cadence engine everything else uses, so replies stop the follow-ups and
    // the reporting comes out with the day numbers already on it.
    if (followups.length === 0) {
      api
        .createBlast({
          name,
          rule,
          template_id: templateId,
          rate_per_minute: rate,
          limit: cap,
          exclusions: skip,
          body: templateId ? undefined : draft.trim(),
          personalize,
          personalize_brief: personalize ? brief.trim() : undefined,
          scheduled_at: startAt ? etToUtc(startAt) : undefined,
          build_at_send: startAt ? buildAtSend : undefined,
          standby: standby || undefined,
        })
        .then(() => navigate("/blasts"))
        .catch((e) => {
          setError(e.message);
          setBusy(false);
        });
      return;
    }

    // Day 0 opens at the time the operator asked for, not at a hardcoded
    // noon. This branch used to ignore the start time completely - it was
    // only read on the single-message path above - so a blast scheduled for
    // 3pm went out at 12:00 and nobody could see why.
    const dayZeroAt = startAt ? startAt.split("T")[1].slice(0, 5) : "11:00";

    // Which day each message lands on: a same-day follow-up shares the day
    // of the message before it.
    const days: number[] = [];
    followups.forEach((f, i) => {
      const prev = i === 0 ? 0 : days[i - 1];
      days.push(f.mode === "after" ? prev : f.day);
    });

    const steps = [
      {
        name: `Day 0 ${dayZeroAt}`,
        day_offset: 0,
        delay_from: "day_time",
        at_time: dayZeroAt,
        delay_minutes: 0,
        body,
        send_window_start: `${dayZeroAt}:00`,
      },
      ...followups.map((f, i) =>
        f.mode === "after"
          ? {
              // Minutes from when the message before it actually went out,
              // for each person - not a clock time. A clock time here used
              // to be turned into minutes from the first message's planned
              // time, so sending the first one late dragged the rest.
              name: `Day ${days[i]} follow-up ${fmtDelay(f.after)} later`,
              day_offset: days[i],
              delay_from: "previous",
              at_time: undefined,
              delay_minutes: f.after,
              body: f.body,
              send_window_start: undefined,
            }
          : {
              name: `Day ${f.day} ${f.at}`,
              day_offset: f.day,
              delay_from: "day_time",
              at_time: f.at,
              delay_minutes: 0,
              body: f.body,
              send_window_start: `${f.at}:00`,
            },
      ),
    ].map((step) => ({
      ...step,
      template_id: null,
      // Every step of the sequence, not just the first. Without this the
      // tick box above quietly applied to nothing on this path: a
      // three-message sequence built here went out as three templates
      // while the screen said each one would be written individually.
      personalize,
      personalize_brief: personalize ? brief.trim() : undefined,
    }));

    // The same audience started a moment ago is how the same people get
    // texted twice (22 Sep 2026: the first enrolment looked stuck, so it was
    // made again). Asked BEFORE a campaign is created, so a second one is
    // not even made by mistake.
    api
      .sameAudience(rule, skip, cap)
      .then((check) => {
        if (check.same && !window.confirm(`${check.message}\n\nStart it anyway?`)) {
          throw new Error("Not started - that audience is already in a campaign (see Campaigns).");
        }
        return check.same;
      })
      .then((confirmedDuplicate) => api
      .createCampaign({
        name: `One-Off ${name}`,
        campaign_type: "one_off",
        // "at_time" so it honours the hour that was asked for. A one-off has
        // no welcome to be prompt about; the point of scheduling it is the
        // hour.
        // "at_time" only when a start time was actually asked for. With no
        // start time the anchor falls back to 11:00, and at_time would then
        // read a passed 11:00 as "wait for tomorrow" - so pressing "send as
        // soon as I launch it" would have sent nothing until the morning.
        first_message_mode: startAt ? "at_time" : "immediate",
        steps,
      })
      .then((c) => api.publishCampaign(c.id).then(() => c))
      .then((c) => api.enrollAudience(c.id, rule, skip, cap, rate, confirmedDuplicate)))
      // a large audience carries on in the background; Campaigns shows it
      .then(() => navigate("/campaigns"))
      .catch((e) => {
        setError(e.message);
        setBusy(false);
      });
  };

  return (
    <div className="sidebar">
      <div className="col">
        <ErrorBanner error={error} />

        <Card title="1 · Who gets it">
          <div className="row w">
            <span className="mono">{fmt.n(willSend)} will receive it</span>
            {preview && preview.excluded_total > 0 && (
              <span className="dim">
                · {fmt.n(preview.excluded_total)} held back (
                {Object.entries(preview.excluded)
                  .map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`)
                  .join(", ")}
                )
              </span>
            )}
          </div>

          {noConsent > 0 && (
            <div className="banner warn stack" style={{ marginTop: 12 }}>
              <b>
                {fmt.n(noConsent)} have no consent on file
                {reachable === 0 ? ", so nobody can be messaged yet" : ""}.
              </b>{" "}
              Imported contacts always start that way — an import is deliberately not
              allowed to opt people in, because a stray spreadsheet column must never
              be able to do that. If you already hold their consent, say where it came
              from and they become reachable.
              <div className="row w" style={{ marginTop: 10, gap: 8 }}>
                <input
                  className="f"
                  style={{ flex: "1 1 200px", minWidth: 0 }}
                  placeholder="Where did the consent come from?"
                  value={consentSource}
                  onChange={(e) => setConsentSource(e.target.value)}
                />
                <button
                  className="btn p"
                  disabled={consentSource.trim().length < 3 || consenting}
                  onClick={grantConsent}
                >
                  {consenting ? "…" : `Record consent for ${fmt.n(noConsent)}`}
                </button>
              </div>
              <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
                e.g. "web signup form 2024–25" or "opt-in carried over from GHL".
                Anyone who opted out is skipped. Every change is audited against your
                name.
              </div>
            </div>
          )}

          {reachable === 0 && noConsent === 0 && preview && (
            <div className="banner warn stack" style={{ marginTop: 12 }}>
              <b>Nobody can be messaged.</b> Everyone matching this rule is held back
              for a reason shown above. Widen the audience or clear the reason.
            </div>
          )}

          {impact && reachable > 0 && (
            <div style={{ marginTop: 14, borderTop: "1px solid var(--line2)", paddingTop: 12 }}>
              <span className="lab">What this would collide with</span>

              {impact.in_campaign.count > 0 && (
                <div
                  className={`banner ${impact.in_campaign.percent >= 20 ? "warn" : ""} stack`}
                  style={{ marginTop: 8 }}
                >
                  <b>
                    {fmt.n(impact.in_campaign.count)} ({impact.in_campaign.percent}%) are
                    part-way through a campaign.
                  </b>{" "}
                  They would get this on top of their next scheduled message, possibly
                  the same day.
                  <div style={{ marginTop: 6 }}>
                    {impact.in_campaign.by_campaign.map((c) => (
                      <div className="brk" key={c.name}>
                        <span className="dim">{c.name}</span>
                        <span className="mono">{fmt.n(c.count)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ marginTop: 8 }}>
                {[
                  {
                    n: impact.messaged_24h,
                    label: "already had a message in the last 24 hours",
                    note: "the frequency cap will silently drop these",
                    warn: impact.messaged_24h.percent >= 25,
                  },
                  {
                    n: impact.already_queued,
                    label: "already have something queued",
                    note: "waiting to send from another source",
                    warn: impact.already_queued.percent >= 20,
                  },
                  {
                    n: impact.in_another_blast,
                    label: "are in another blast that has not finished",
                    note: "they would get both",
                    warn: impact.in_another_blast.count > 0,
                  },
                  {
                    n: impact.never_messaged,
                    label: "have never been messaged before",
                    note: "first impression — worth the better copy",
                    warn: false,
                  },
                ]
                  .filter((r) => r.n.count > 0)
                  .map((r) => (
                    <div className="brk" key={r.label}>
                      <span>
                        <b className="mono" style={{ color: r.warn ? "var(--warn)" : undefined }}>
                          {fmt.n(r.n.count)}
                        </b>{" "}
                        <span className="dim">{r.label}</span>
                        <div className="dim" style={{ fontSize: 11 }}>
                          {r.note}
                        </div>
                      </span>
                      <span className="mono dim">{r.n.percent}%</span>
                    </div>
                  ))}
              </div>

              <div style={{ marginTop: 12 }}>
                <span className="lab">Leave people out</span>
                <div className="col" style={{ gap: 5, marginTop: 6 }}>
                  {(impact.available_exclusions ?? []).map((x) => (
                    <label
                      key={x.key}
                      className="row"
                      style={{ gap: 8, fontSize: 12.5, cursor: "pointer" }}
                    >
                      <input
                        type="checkbox"
                        checked={!!skip[x.key]}
                        onChange={(e) =>
                          setSkip((s) => ({ ...s, [x.key]: e.target.checked }))
                        }
                      />
                      <span>{x.label}</span>
                    </label>
                  ))}
                </div>
                {impact.removed > 0 && (
                  <div className="banner ok stack" style={{ marginTop: 10 }}>
                    <b>
                      {fmt.n(impact.removed)} removed — {fmt.n(impact.after_exclusions)} of{" "}
                      {fmt.n(impact.reachable)} will get this.
                    </b>{" "}
                    They stay eligible and will be picked up by their campaign or a
                    later blast as normal.
                  </div>
                )}
              </div>

              {impact.timezones.length > 1 && (
                <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
                  Across {impact.timezones.length} timezones —{" "}
                  {impact.timezones
                    .slice(0, 3)
                    .map((t) => `${t.zone.split("/")[1]?.replace("_", " ")} ${t.count}`)
                    .join(", ")}
                  . They are all judged against the same window, 11am to 8pm Eastern,
                  and not against their own clocks
                  {(() => {
                    // The old sentence promised each person their own local
                    // window, and put a number on the spread that was really
                    // the count of zones minus one. Both were false: one
                    // Eastern window means the western end is texted early in
                    // its own morning, and that is the thing worth saying.
                    const first = localAtWindowOpen(impact.timezones);
                    if (!first || first.at >= "10:00") return ".";
                    const label = first.zone.split("/")[1]?.replace("_", " ") ?? first.zone;
                    return `, so when it opens it is ${first.at} for the ${fmt.n(
                      first.count,
                    )} in ${label}.`;
                  })()}
                </div>
              )}
            </div>
          )}
        </Card>

        <Card
          title="2 · What it says"
          footer={
            <span className="dim">
              {personalize
                ? "Each message is written just before it is due, from their conversation with us — what they have said, what we have sent, and our notes on them. If one cannot be written, the fallback below goes out on time instead, so nothing waits and nobody is skipped."
                : "Whatever you type is saved as a template automatically, so its results can be compared against other wording later. You never have to make one first."}
            </span>
          }
        >
          <div className="col">
            <label
              className="row"
              style={{ gap: 8, fontSize: 12.5, cursor: "pointer" }}
            >
              <input
                type="checkbox"
                checked={personalize}
                onChange={(e) => {
                  setPersonalize(e.target.checked);
                  setSamples(null);
                }}
              />
              <b>Send customized messages</b>
              <span className="dim">
                each message written for that person, from their own contact
              </span>
            </label>

            {personalize && (
              <>
                <div>
                  <span className="lab">What is this send about?</span>
                  <textarea
                    className="f"
                    style={{ minHeight: 64, marginTop: 6 }}
                    placeholder="Black Friday, the best offer we've run all year. We want calls booked this week."
                    value={brief}
                    onChange={(e) => setBrief(e.target.value)}
                  />
                  <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                    Sets the occasion and the angle. It cannot loosen the rules:
                    no place or route, no price or discount, no link, and never a
                    conversation we did not have — whatever you write here.
                  </div>
                </div>

                <div className="row w">
                  <button
                    className="btn s"
                    disabled={sampling || !reachable}
                    onClick={() => {
                      setSampling(true);
                      setSampleError(null);
                      api
                        .personalizedPreview(rule, brief.trim(), 4)
                        .then((r) => setSamples(r.drafts))
                        .catch((e) => setSampleError(e.message))
                        .finally(() => setSampling(false));
                    }}
                  >
                    {sampling ? "Writing… (about 10s)" : "Show me four real ones"}
                  </button>
                  <span className="dim" style={{ fontSize: 11.5 }}>
                    {/* Measured at 7-11 seconds in production: four messages
                        are written one at a time against each person's real
                        history. Saying so is the difference between waiting
                        and assuming it has hung. */}
                    Four actual people from this audience, each written from
                    scratch — it takes about ten seconds. Nothing is sent and
                    nothing is saved.
                  </span>
                </div>
                {sampleError && <div className="banner warn stack">{sampleError}</div>}

                {samples && (
                  <div className="col" style={{ gap: 8 }}>
                    {samples.map((s) => (
                      <div
                        key={s.contact_id}
                        style={{
                          border: "1px solid var(--line2)",
                          borderRadius: 8,
                          padding: 10,
                        }}
                      >
                        <div className="row w" style={{ fontSize: 12, gap: 6 }}>
                          <b>{s.name}</b>
                          <Pill tone="nu">{s.where_they_stand.replace(/_/g, " ")}</Pill>
                          <span className="dim">from {s.from}</span>
                          {s.would_send ? (
                            <Pill tone="ok">would send</Pill>
                          ) : (
                            <Pill tone="wa">held back</Pill>
                          )}
                        </div>
                        {s.message && (
                          <div className="bub" style={{ marginTop: 8 }}>
                            {s.message}
                          </div>
                        )}
                        {/* Shown, not hidden. A refusal on screen is the
                            checks doing visibly what they will go on doing
                            unattended, which is the whole question somebody
                            has about this feature. */}
                        {!s.would_send && (
                          <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
                            Rejected: {s.rejected.join("; ")}. It would be
                            rewritten once, then the fallback below would go out.
                          </div>
                        )}
                        {s.would_send && (
                          <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
                            {s.angle} · {s.characters} characters
                          </div>
                        )}
                      </div>
                    ))}
                    <div className="dim" style={{ fontSize: 11.5 }}>
                      About $
                      {(
                        (samples.reduce((t, s) => t + s.cost_usd, 0) /
                          Math.max(1, samples.length)) *
                        willSend
                      ).toFixed(2)}{" "}
                      to write all {fmt.n(willSend)}, on top of the texts themselves.
                    </div>
                  </div>
                )}

                <div style={{ borderTop: "1px solid var(--line2)", paddingTop: 10 }}>
                  <span className="lab">
                    Fallback — goes out to anyone whose message cannot be written
                  </span>
                </div>
              </>
            )}

            {templateId ? (
              <div className="row w">
                <Pill tone="ac">
                  {templates.find((t) => t.id === templateId)?.name ?? "saved template"}
                </Pill>
                <button
                  className="btn s"
                  onClick={() => {
                    setTemplateId(null);
                    setDraft("");
                  }}
                >
                  Write my own instead
                </button>
              </div>
            ) : (
              <>
                <textarea
                  className="f"
                  style={{ minHeight: 92 }}
                  placeholder={
                    personalize
                      ? "Optional. Leave it empty and we'll fall back to a plain message that is true of everybody."
                      : "Type the message. Use {{first_name}} for their name."
                  }
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                />
                {templates.length > 0 && (
                  <select
                    className="f"
                    value=""
                    onChange={(e) =>
                      e.target.value && setTemplateId(Number(e.target.value))
                    }
                  >
                    <option value="">or reuse something you've sent before…</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                )}
              </>
            )}

            {body && (
              <>
                <div className="bub">{body}</div>
                {cost && (
                  <div className="row w" style={{ fontSize: 12.5 }}>
                    <span className="dim">{cost.characters} characters</span>
                    <Pill tone={cost.is_unicode ? "wa" : "nu"}>
                      {cost.segments} text{cost.segments > 1 ? "s" : ""} each
                    </Pill>
                    <b className="mono">${cost.cost_usd.toFixed(2)}</b>
                    <span className="dim">for {fmt.n(willSend)} people</span>
                  </div>
                )}
                {cost?.warning && <div className="banner warn stack">{cost.warning}</div>}
              </>
            )}

            <div style={{ borderTop: "1px solid var(--line2)", paddingTop: 12 }}>
              {/* The first message's timing lives in the Schedule card, not
                  here - which meant every follow-up showed a time and the
                  message they follow showed none, and the obvious reading was
                  "the first one is instant". Say it where the question is
                  asked. */}
              <div className="crow" style={{ marginBottom: 8, background: "var(--surf)" }}>
                <span className="lab">Day 0</span>
                <b className="mono">
                  {startAt
                    ? new Date(startAt).toLocaleString(undefined, {
                        weekday: "short", hour: "2-digit", minute: "2-digit",
                      }) + " Eastern"
                    : "as soon as you launch it"}
                </b>
                <span className="dim" style={{ fontSize: 11.5 }}>
                  the message above · change it under Schedule
                </span>
              </div>
              {followups.map((f, i) => {
                const set = (patch: Partial<Followup>) =>
                  setFollowups((fs) => fs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                const prevDay = followups
                  .slice(0, i)
                  .reduce((d, x) => (x.mode === "after" ? d : x.day), 0);
                return (
                  <div className="crow" key={i} style={{ alignItems: "flex-start", marginBottom: 8 }}>
                    <div className="col" style={{ gap: 6 }}>
                      <select
                        className="f"
                        value={f.mode}
                        onChange={(e) => set({ mode: e.target.value as Followup["mode"] })}
                      >
                        <option value="after">After the message before it</option>
                        <option value="day">On a later day</option>
                      </select>
                      {f.mode === "after" ? (
                        <div className="row" style={{ gap: 6 }}>
                          <input
                            className="f"
                            type="number"
                            min={15}
                            step={15}
                            style={{ width: 70 }}
                            value={f.after}
                            onChange={(e) => set({ after: Math.max(15, Number(e.target.value) || 15) })}
                          />
                          <span className="dim" style={{ fontSize: 12 }}>
                            minutes later ({fmtDelay(f.after)}), for each person
                          </span>
                        </div>
                      ) : (
                        <div className="row" style={{ gap: 6 }}>
                          <span className="lab">Day</span>
                          <input
                            className="f"
                            type="number"
                            min={prevDay + 1}
                            style={{ width: 56 }}
                            value={f.day}
                            onChange={(e) => set({ day: Math.max(prevDay + 1, Number(e.target.value) || 1) })}
                          />
                          <input
                            className="f"
                            type="time"
                            value={f.at}
                            onChange={(e) => set({ at: e.target.value })}
                          />
                        </div>
                      )}
                    </div>
                    <textarea
                      className="f"
                      style={{ flex: "1 1 280px", minHeight: 56, minWidth: 0 }}
                      placeholder={
                        personalize
                          ? "Fallback for this follow-up — leave empty for a plain one"
                          : "What the follow-up says"
                      }
                      value={f.body}
                      onChange={(e) => set({ body: e.target.value })}
                    />
                    <button
                      className="x"
                      onClick={() => setFollowups((fs) => fs.filter((_, j) => j !== i))}
                    >
                      ×
                    </button>
                  </div>
                );
              })}

              <div className="row w" style={{ gap: 8 }}>
                <button
                  className="btn s"
                  onClick={() =>
                    setFollowups((fs) => [...fs, { mode: "after", after: 90, day: 1, at: "12:00", body: "" }])
                  }
                >
                  + add a follow-up the same day
                </button>
                <button
                  className="btn s"
                  onClick={() =>
                    setFollowups((fs) => {
                      const lastDay = fs.reduce((d, x) => (x.mode === "after" ? d : x.day), 0);
                      return [...fs, { mode: "day", after: 90, day: lastDay + 1, at: "12:00", body: "" }];
                    })
                  }
                >
                  + add a follow-up on a later day
                </button>
              </div>

              {followups.length > 0 && (
                <div className="banner stack" style={{ marginTop: 10, background: "var(--surf2)" }}>
                  <b>
                    This is now a {followups.length + 1}-message send
                    {(() => {
                      const lastDay = followups.reduce((d, x) => (x.mode === "after" ? d : x.day), 0);
                      return lastDay > 0 ? ` over ${lastDay + 1} days` : " on one day";
                    })()}
                    .
                  </b>{" "}
                  Anyone who replies <b>stops receiving the rest</b>. A same-day follow-up goes the
                  set time after that person's previous message. If it would land after 8 PM ET:
                  on the first day it moves to the next sending morning, and on a later day it is
                  skipped. You will find it under Campaigns, named “One-Off {name || "…"}”.
                </div>
              )}
            </div>
          </div>
        </Card>

        <Card title="3 · How fast">
          <div className="col">
            <div className="row w" style={{ gap: 8 }}>
              <span className="lab">Start</span>
              <button
                className={`btn s${startAt ? "" : " p"}`}
                onClick={() => setStartAt("")}
              >
                as soon as I launch it
              </button>
              <span className="dim">or</span>
              <input
                className="f"
                type="datetime-local"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
              />
              <span className="dim" style={{ fontSize: 11.5 }}>
                Eastern
              </span>
            </div>
            {startAt && followups.length === 0 && (
              <label className="row" style={{ gap: 7, fontSize: 12.5, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={buildAtSend}
                  onChange={(e) => setBuildAtSend(e.target.checked)}
                />
                <span>
                  <b>Build the list at the send time</b> — include everyone who matches
                  the audience <i>then</i>, not just who matches now
                </span>
              </label>
            )}
            {startAt && (
              <div className="dim" style={{ fontSize: 11.5, marginTop: -4 }}>
                {buildAtSend && followups.length === 0
                  ? "Nothing is chosen or queued now. At the send time Five9 builds the list from everyone who matches then and sends it — once you press Schedule it on the Blasts screen. The count above is only who matches now."
                  : "The list is built now and held until then. Still subject to the 11am–8pm Eastern window, so a start outside it waits for the window."}
              </div>
            )}

            {followups.length === 0 && (
              <label className="row" style={{ gap: 7, fontSize: 12.5, cursor: "pointer", marginTop: 6 }}>
                <input
                  type="checkbox"
                  checked={standby}
                  disabled={personalize}
                  onChange={(e) => setStandby(e.target.checked)}
                />
                <span>
                  <b>Standby — only when the team has room</b>. The autopilot sends these to fill the gaps so
                  nobody on the phones is idle, after every other text and never all at once. Needs the
                  autopilot on; the rate below does not apply.{personalize ? " (Not with individually written messages.)" : ""}
                </span>
              </label>
            )}

            <div className="row w" style={{ gap: 6, marginTop: 8 }}>
              <span className="lab">Rate</span>
              {[1, 5, 10, 20, 60, 120, 240, 600].map((r) => (
                <button
                  key={r}
                  className={`btn s${rate === r ? " p" : ""}`}
                  onClick={() => setRate(r)}
                >
                  {r}
                </button>
              ))}
              <input
                className="f"
                type="number"
                min={1}
                max={600}
                style={{ width: 84 }}
                value={rate}
                onChange={(e) =>
                  setRate(Math.max(1, Math.min(600, Number(e.target.value) || 1)))
                }
              />
              <span className="dim" style={{ fontSize: 12 }}>
                per minute
              </span>
            </div>

            <div className="row w" style={{ fontSize: 12.5 }}>
              <b className="mono">{effectiveRate} / min</b>
              <span className="dim">·</span>
              <span>
                {minutes < 60
                  ? `about ${minutes} minute${minutes > 1 ? "s" : ""} of sending`
                  : `about ${(minutes / 60).toFixed(1)} hours of sending`}
              </span>
              {rate <= 5 && (
                <span className="dim">— slow on purpose, good for a first run</span>
              )}
            </div>

            {/* The rate box is a ceiling, not an accelerator. Asking for more
                than the pipe can do does not make it faster - it just makes
                rows due sooner than anyone can send them - so the estimate is
                built on measured throughput and this says so rather than
                quietly using a different number than the one on screen. */}
            {rateIsWishful && capacity && (
              <div className="dim" style={{ fontSize: 12 }}>
                Sending has really been managing about{" "}
                <b className="mono">{capacity.sustained_per_minute}/min</b>{" "}
                (best minute {capacity.peak_per_minute}, measured over the last{" "}
                {capacity.measured_over_days} days), so {fmt.n(willSend)} people
                will take about {minutes < 60
                  ? `${minutes} minutes`
                  : `${(minutes / 60).toFixed(1)} hours`}{" "}
                whatever is set here. Asking for more only queues them up
                sooner. More send workers is what moves this number.
              </div>
            )}

            <span className="dim" style={{ fontSize: 12 }}>
              Nothing sends outside 11am to 8pm Eastern, whatever speed you pick — so a
              rate that would run past 8pm simply continues the next day.
            </span>

            <div style={{ borderTop: "1px solid var(--line2)", paddingTop: 12 }}>
              <span className="lab">Send to how many</span>
              <div className="row w" style={{ gap: 6, marginTop: 6 }}>
                {[null, 100, 500, 1000].map((n) => (
                  <button
                    key={String(n)}
                    className={`btn s${cap === n ? " p" : ""}`}
                    onClick={() => setCap(n)}
                    disabled={n !== null && n >= reachable}
                  >
                    {n === null ? `All ${fmt.n(reachable)}` : `First ${fmt.n(n)}`}
                  </button>
                ))}
                <input
                  className="f"
                  type="number"
                  min={1}
                  max={reachable || undefined}
                  placeholder="or type a number"
                  style={{ width: 150 }}
                  value={cap ?? ""}
                  onChange={(e) =>
                    setCap(e.target.value ? Number(e.target.value) : null)
                  }
                />
              </div>
              {cap !== null && cap < reachable && (
                <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
                  The other {fmt.n(reachable - cap)} are untouched and stay eligible —
                  run the same audience again later to reach them. The slice is the
                  first {fmt.n(cap)} by contact id, not a random sample, so re-running
                  this exact blast hits the same people rather than a fresh set.
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      <Card title="Ready to send">
        <div className="col" style={{ gap: 12 }}>
          <div
            className="num"
            style={{ fontSize: 33, fontWeight: 650, letterSpacing: "-0.03em" }}
          >
            {fmt.n(willSend)}
          </div>
          {cap !== null && cap < reachable && (
            <span className="dim" style={{ fontSize: 11.5, marginTop: -8 }}>
              of {fmt.n(reachable)} who match
            </span>
          )}

          <input
            className="f"
            placeholder="Name this blast"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="f"
            placeholder={`Type ${willSend} to confirm`}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
          />

          <button
            className="btn p"
            // A personalised blast does not need copy typed into it: every
            // message is written individually, and the server fills in a
            // plain fallback for anyone whose message cannot be.
            disabled={!confirmed || !name || (!body.trim() && !personalize) || busy}
            onClick={create}
          >
            {busy ? "Creating…" : "Create the blast"}
          </button>

          <span className="dim" style={{ fontSize: 11.5 }}>
            Creating it builds the list. Nothing sends until you press Send on the next
            screen, and you can stop it at any point — though messages already sent cannot
            be recalled.
          </span>
        </div>
      </Card>
    </div>
  );
}
